<template>
  <div class="hotel-list">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ $t('hotels.title') }}</h2>
      <b-button variant="primary" @click="createHotel">
        <i class="fas fa-plus mr-2"></i>{{ $t('hotels.create_button') }}
      </b-button>
    </div>

    <CrudTable :columns="fields" :data-sources="hotels" :pagination="paginationInfo" :loader="isLoading"
      list-url="/hotels" @search="handleSearch" @filter="handleFilter" @refresh="fetchHotels" ref="action-table">
      <!-- Name Column -->
      <template #cell(name)="data">
        <div class="d-flex align-items-center">
          <!-- Room Type Images -->
          <div class="room-images me-3" v-if="data.item.galleries && data.item.galleries.length > 0">
            <div class="image-preview">
              <img :src="data.item.galleries[0].path" :alt="data.item.name" class="room-type-image"
                @click="showImageGallery(data.item.galleries, data.item.name)" />
              <div class="image-count" v-if="data.item.galleries.length > 1">
                +{{ data.item.galleries.length - 1 }}
              </div>
            </div>
          </div>
          <div class="room-info">
            <div class="font-weight-bold">{{ data.item.name || 'N/A' }}</div>
            <small class="text-muted">{{ data.item.description || '' }}</small>
          </div>
        </div>
      </template>
      <template #cell(contact_email)="data">
        <div>{{ data.item.contact_email || 'N/A' }}</div>
      </template>
      <template #cell(phone)="data">
        <div>{{ data.item.phone || 'N/A' }}</div>
      </template>
      <template #cell(rating)="data">
        <div>{{ data.item.rating || 'N/A' }}</div>
      </template>
      <template #cell(check_in_time)="data">
        <div>{{ data.item.check_in_time || 'N/A' }}</div>
      </template>
      <template #cell(check_out_time)="data">
        <div>{{ data.item.check_out_time || 'N/A' }}</div>
      </template>
      <template #cell(address)="data">
        <div>{{ data.item.address || 'N/A' }}</div>
      </template>
      <template #cell(city)="data">
        <div>{{ data.item.city?.name || 'N/A' }}</div>
      </template>
      <template #cell(country)="data">
        <div>{{ data.item.country?.name || 'N/A' }}</div>
      </template>
      <template #cell(latitude)="data">
        <div>{{ data.item.latitude || 'N/A' }}</div>
      </template>
      <template #cell(longitude)="data">
        <div>{{ data.item.longitude || 'N/A' }}</div>
      </template>
      <template #cell(is_active)="data">
        <div>{{ data.item.is_active ? $t('hotels.status_active') : $t('hotels.status_inactive') }}</div>
      </template>
      <template #cell(hotel_group)="data">
        <div>{{ data.item.hotel_group?.name || 'N/A' }}</div>
      </template>
      <template #cell(location)="data">
  <div>
    {{ data.item.country?.name || '' }}
    <template v-if="data.item.state">, {{ data.item.state.name }}</template>
    <template v-if="data.item.city">, {{ data.item.city.name }}</template>
  </div>
</template>

      <!-- Actions Column -->
      <template #cell(actions)="data">
        <div class="d-flex gap-2">
          <b-button variant="outline-primary" size="sm" @click="editHotel(data.item)">
            <i class="fas fa-edit"></i>
          </b-button>
          <b-button variant="outline-danger" size="sm" @click="confirmDelete(data.item)">
            <i class="fas fa-trash"></i>
          </b-button>
        </div>
      </template>
      <template #cell(booking_confirmation)="data">
        <div class="text-capitalize">{{ data.item.booking_confirmation || 'N/A' }}</div>
      </template>

    </CrudTable>

    <!-- Delete Confirmation Modal -->
    <b-modal v-model="showDeleteModal" :title="$t('hotels.delete_modal_title')" @ok="handleDelete" ok-variant="danger">
      {{ $t('hotels.delete_modal_message') }}
    </b-modal>
    <!-- Image Gallery Modal -->
    <b-modal v-model="showImageModal" :title="imageModalTitle" size="lg" centered>
      <div class="image-gallery">
        <div class="gallery-container">
          <div v-for="(image, index) in selectedImages" :key="index" class="gallery-item">
            <img :src="image.path" :alt="imageModalTitle" class="gallery-image" />
          </div>
        </div>
      </div>
      <template #modal-footer>
        <b-button variant="secondary" @click="showImageModal = false">
          {{ $t('common.close') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { request } from "@/Util/Request";
import CrudTable from "@/components/CrudTable.vue";

export default {
  name: 'HotelList',
  components: {
    CrudTable
  },

  data() {
    return {
      hotels: [],
      isLoading: false,
      showDeleteModal: false,
      selectedHotel: null,
      currentPage: 1,
      perPage: 10,
      sortBy: 'created_at',
      sortDesc: true,
      showImageModal: false,
      selectedImages: [],
      imageModalTitle: '',
      filters: {
        search: '',
        status: null
      },
      fields: [
        { key: 'name', label: this.$t('hotels.table_header_name'), sortable: true, sortDirection: 'asc' },
        { key: 'hotel_group', label: this.$t('hotels.table_header_hotel_group'), sortable: true },
        { key: 'contact_email', label: this.$t('hotels.table_header_contact_email') },
        { key: 'phone', label: this.$t('hotels.table_header_phone') },
        { key: 'rating', label: this.$t('hotels.table_header_rating') },
        { key: 'check_in_time', label: this.$t('hotels.table_header_check_in_time') },
        { key: 'check_out_time', label: this.$t('hotels.table_header_check_out_time') },
        { key: 'address', label: this.$t('hotels.table_header_address') },
        { key: 'location', label: this.$t('hotels.table_header_location') },
        { key: 'latitude', label: this.$t('hotels.table_header_latitude') },
        { key: 'longitude', label: this.$t('hotels.table_header_longitude') },
        { key: 'booking_confirmation', label: this.$t('hotels.table_header_booking_confirmation') },
        { key: 'is_active', label: this.$t('hotels.table_header_status'), sortable: true, sortDirection: 'asc' },
        { key: 'actions', label: this.$t('common.actions') }
      ],
      statusOptions: [
        { value: null, text: this.$t('hotels.status_all') },
        { value: 1, text: this.$t('hotels.status_active') },
        { value: 0, text: this.$t('hotels.status_inactive') }
      ],
      paginationInfo: {
        from: 0,
        to: 0,
        total: 0
      }
    }
  },

  methods: {
    showImageGallery(images, hotelName) {
      this.selectedImages = images;
      this.imageModalTitle = `${hotelName} - Images`;
      this.showImageModal = true;
    },
    handleSearch(search) {
      this.filters.search = search;
      this.currentPage = 1;
      this.fetchHotels();
    },

    handleFilter(filter) {
      this.filters = { ...this.filters, ...filter };
      this.currentPage = 1;
      this.fetchHotels();
    },

    createHotel() {
      this.$router.push('/admin/hotels/create');
    },

    editHotel(hotel) {
      this.$router.push(`/admin/hotels/${hotel.id}/edit`);
    },

    confirmDelete(hotel) {
      this.selectedHotel = hotel;
      this.showDeleteModal = true;
    },

    async handleDelete() {
      try {
        await request({
          method: 'delete',
          url: `/hotels/${this.selectedHotel.id}`
        });
        this.$refs['action-table'].refreshTableData();
        const successMessage = this.$t("hotels.delete_success_message")
        this.notifySuccessWithMsg(successMessage);
        this.fetchHotels();
      } catch (error) {
        console.error('Error deleting hotel:', error);
      }
    },

    async fetchHotels() {
      try {
        this.isLoading = true;
        const response = await request({
          method: 'get',
          url: '/hotels',
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            sort_by: this.sortBy,
            sort_desc: this.sortDesc,
            ...this.filters
          }
        });

        if (response.data && response.data.data) {
          this.hotels = response.data.data;
          if (response.data.meta) {
            this.paginationInfo = {
              from: response.data.meta.from || 0,
              to: response.data.meta.to || 0,
              total: response.data.meta.total || 0
            };
          }
        } else {
          this.hotels = [];
          this.paginationInfo = {
            from: 0,
            to: 0,
            total: 0
          };
        }
      } catch (error) {
        console.error('Error fetching hotels:', error);
        this.hotels = [];
        this.paginationInfo = {
          from: 0,
          to: 0,
          total: 0
        };
      } finally {
        this.isLoading = false;
      }
    }
  },

  created() {
    this.fetchHotels();
  }
}
</script>

<style scoped>
.hotel-list {
  padding: 20px;
}

.room-images {
  position: relative;
}

.image-preview {
  position: relative;
  cursor: pointer;
  transition: transform 0.2s;
}

.image-preview:hover {
  transform: scale(1.05);
}

.room-type-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e9ecef;
}

.image-count {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.room-info {
  flex: 1;
}

.image-gallery {
  max-height: 500px;
  overflow-y: auto;
}

.gallery-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  padding: 10px;
}

.gallery-item {
  text-align: center;
}

.gallery-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: transform 0.2s;
}

.gallery-image:hover {
  transform: scale(1.02);
}
</style>